import BlogArticleLayout from '../components/BlogArticleLayout';

export default function GesundheitsBlogArticle({ darkMode = false }) {
  const article = {
    title: "Bandscheibenvorfall vorbeugen: Gesund durch den 9-Stunden-Büroalltag",
    excerpt: "Entdecken Sie effektive Strategien und praktische Übungen, um Rückenproblemen trotz langem Sitzen im Büro vorzubeugen.",
    author: "Nele Schumacher",
    authorBio: "Orthopädin und Expertin für Arbeitsplatzergonomie mit über 12 Jahren Erfahrung in der Prävention von Rückenleiden.",
    date: "2025-06-03",
    readTime: "10 min",
    category: "Gesundheit",
    tags: ["Rückengesundheit", "Büroarbeit", "Prävention", "Ergonomie", "Übungen", "Gesundheit"],
    image: "from-green-400 to-blue-600",
    views: 2847,
    likes: 156,
    comments: 42,
    tableOfContents: [
      { id: "introduction", title: "Das Büro-Dilemma: Warum Rückenprobleme entstehen" },
      { id: "ergonomie", title: "Ergonomischer Arbeitsplatz: Die Basis" },
      { id: "bewegung", title: "Bewegung im Büroalltag integrieren" },
      { id: "uebungen", title: "Gezielte Übungen für zwischendurch" },
      { id: "pausen", title: "Richtige Pausengestaltung" },
      { id: "lifestyle", title: "Lifestyle-Faktoren für eine gesunde Wirbelsäule" },
      { id: "warnsignale", title: "Warnsignale erkennen" },
      { id: "conclusion", title: "Ihr Weg zu einem gesunden Rücken" }
    ]
  };

  return (
    <BlogArticleLayout darkMode={darkMode} article={article}>

      <section id="introduction">
        <h2>Das Büro-Dilemma: Warum Rückenprobleme entstehen</h2>
        <div className="callout">
          <h3 className="font-bold mb-2 text-red-600">⚠️ Alarmierende Zahlen:</h3>
          <p>
            80% aller Deutschen leiden mindestens einmal im Leben unter Rückenschmerzen. Bei Büroarbeitern steigt das Risiko um 40% gegenüber körperlich aktiven Berufen.
          </p>
        </div>
        <p>
          Neun Stunden täglich im Büro zu verbringen ist für viele von uns Realität. Doch unser Körper ist nicht für stundenlanges Sitzen konzipiert. Die Wirbelsäule, insbesondere die Bandscheiben, leiden unter der dauerhaften Belastung in derselben Position.
        </p>
        <p>
          Beim Sitzen lastet etwa 40% mehr Druck auf den Bandscheiben als beim Stehen. Hinzu kommen oft schlechte Haltung, unergonomische Arbeitsplätze und Bewegungsmangel. Das Ergebnis: Verspannungen, Schmerzen und im schlimmsten Fall ein Bandscheibenvorfall.
        </p>
        <p>
          Aber es gibt gute Nachrichten: Mit den richtigen Strategien können Sie Rückenproblemen effektiv vorbeugen und trotz langem Sitzen gesund bleiben. In diesem Artikel zeigen wir Ihnen, wie es geht.
        </p>
      </section>

      <section id="ergonomie">
        <h2>Ergonomischer Arbeitsplatz: Die Basis</h2>
        <p>
          Ein ergonomisch eingerichteter Arbeitsplatz ist das Fundament für einen gesunden Rücken. Bereits kleine Anpassungen können große Wirkung zeigen:
        </p>

        <div className="grid-2">
          <div className="highlight-box">
            <h3 className="font-bold mb-3 text-green-600">🪑 Der richtige Stuhl</h3>
            <ul>
              <li>• Verstellbare Sitzhöhe (Füße flach auf dem Boden)</li>
              <li>• Lordosenstütze für die natürliche Wirbelsäulenkrümmung</li>
              <li>• Armlehnen in Ellbogenhöhe</li>
              <li>• Sitztiefe: 2-3 Finger breit Platz zur Kniekehle</li>
            </ul>
          </div>
          <div className="highlight-box">
            <h3 className="font-bold mb-3 text-blue-600">💻 Monitor-Position</h3>
            <ul>
              <li>• Oberkante des Bildschirms auf Augenhöhe</li>
              <li>• Abstand: 50-70 cm zum Bildschirm</li>
              <li>• Gerader Blick, leicht nach unten geneigt</li>
              <li>• Bei mehreren Monitoren: Hauptmonitor mittig</li>
            </ul>
          </div>
        </div>

        <div className="callout">
          <h3 className="font-bold mb-2 text-yellow-600">💡 Ergonomie-Check:</h3>
          <p>
            Machen Sie alle 2 Wochen einen Ergonomie-Check: Sitzen Sie aufrecht? Sind Ihre Schultern entspannt? Schauen Sie gerade auf den Monitor?
          </p>
        </div>
      </section>

      <section id="bewegung">
        <h2>Bewegung im Büroalltag integrieren</h2>
        <p>
          Das Zauberwort heißt: Bewegung! Schon kleine Aktivitäten zwischendurch können die Belastung für Ihre Wirbelsäule deutlich reduzieren.
        </p>

        <div className="grid-3">
          <div className="highlight-box" style={{textAlign:'center'}}>
            <div className="text-3xl mb-2">⏰</div>
            <h4 className="font-bold mb-2">30-Minuten-Regel</h4>
            <p>Alle 30 Minuten für 1-2 Minuten aufstehen und sich bewegen</p>
          </div>
          <div className="highlight-box" style={{textAlign:'center'}}>
            <div className="text-3xl mb-2">🚶</div>
            <h4 className="font-bold mb-2">Aktive Pausen</h4>
            <p>Treppe statt Aufzug, Gang zum Drucker, Steh-Meetings</p>
          </div>
          <div className="highlight-box" style={{textAlign:'center'}}>
            <div className="text-3xl mb-2">💧</div>
            <h4 className="font-bold mb-2">Wasser-Trick</h4>
            <p>Kleines Glas verwenden - zwingt zu regelmäßigen Wasserpausen</p>
          </div>
        </div>

        <h3>Dynamisches Sitzen</h3>
        <p>
          Wechseln Sie bewusst Ihre Sitzposition alle 15-20 Minuten. Nutzen Sie:
        </p>
        <ul>
          <li className="flex items-start">
            <span className="text-green-500 mr-2 mt-2 text-lg">•</span>
            <span><strong>Vordere Sitzkante:</strong> Aktiviert die Rumpfmuskulatur</span>
          </li>
          <li className="flex items-start">
            <span className="text-green-500 mr-2 mt-2 text-lg">•</span>
            <span><strong>Lehne nutzen:</strong> Entspannt zwischendurch</span>
          </li>
          <li className="flex items-start">
            <span className="text-green-500 mr-2 mt-2 text-lg">•</span>
            <span><strong>Balancekissen:</strong> Trainiert nebenbei die Tiefenmuskulatur</span>
          </li>
        </ul>
      </section>

      <section id="uebungen">
        <h2>Gezielte Übungen für zwischendurch</h2>
        <p>
          Diese Übungen können Sie direkt am Arbeitsplatz durchführen - diskret und effektiv:
        </p>

        <div className="highlight-box">
          <h3 className="font-bold mb-3 text-green-600">🔄 Wirbelsäulen-Mobilisation (2 Min.)</h3>
          <ol className="list-decimal ml-6">
            <li>Aufrecht hinsetzen, Füße flach auf dem Boden</li>
            <li>Schultern 5x vorwärts, 5x rückwärts kreisen</li>
            <li>Kopf langsam links-rechts drehen (je 5x)</li>
            <li>Oberkörper sanft links-rechts rotieren (je 5x)</li>
          </ol>
        </div>

        <div className="highlight-box">
          <h3 className="font-bold mb-3 text-blue-600">💪 Rumpf-Aktivierung (3 Min.)</h3>
          <ol className="list-decimal ml-6">
            <li>Bauchmuskeln anspannen, 10 Sekunden halten, entspannen (5x)</li>
            <li>Schulterblätter zusammenziehen, 10 Sekunden halten (5x)</li>
            <li>Beckenboden anspannen, 10 Sekunden halten (5x)</li>
            <li>Ganze Rumpfmuskulatur anspannen wie ein "Korsett" (5x)</li>
          </ol>
        </div>

        <div className="highlight-box">
          <h3 className="font-bold mb-3 text-purple-600">🧘 Entspannungs-Stretching (2 Min.)</h3>
          <ol className="list-decimal ml-6">
            <li>Arme über Kopf strecken, zur Decke ziehen (20 Sek.)</li>
            <li>Oberkörper seitlich neigen, dehnen (je 15 Sek.)</li>
            <li>Kinn zur Brust, Nacken dehnen (20 Sek.)</li>
            <li>Tief einatmen, entspannt ausatmen (5x)</li>
          </ol>
        </div>

        <div className="callout">
          <h3 className="font-bold mb-2 text-yellow-600">⏱️ Timing-Tipp:</h3>
          <p>
            Verknüpfen Sie Übungen mit festen Zeiten: Bei jedem Kaffee, vor jeder Pause oder nach jedem Meeting. So werden sie zur Gewohnheit.
          </p>
        </div>
      </section>

      <section id="pausen">
        <h2>Richtige Pausengestaltung</h2>
        <p>
          Pausen sind nicht nur zur Erholung da, sondern aktive Investitionen in Ihre Rückengesundheit:
        </p>

        <div className="grid-2">
          <div className="highlight-box">
            <h3 className="font-bold mb-3 text-green-600">☕ Kurze Pausen (5-10 Min.)</h3>
            <ul>
              <li>• Kurzer Spaziergang (auch nur um das Gebäude)</li>
              <li>• Treppe hoch und runter gehen</li>
              <li>• Stehend telefonieren oder E-Mails lesen</li>
              <li>• Leichte Dehnübungen am Fenster</li>
            </ul>
          </div>
          <div className="highlight-box">
            <h3 className="font-bold mb-3 text-blue-600">🍽️ Mittagspause (30-60 Min.)</h3>
            <ul>
              <li>• Spaziergang im Park oder um den Block</li>
              <li>• Kurzes Workout im Fitnessstudio</li>
              <li>• Yoga oder Pilates (auch Online-Videos)</li>
              <li>• Schwimmen oder Radfahren</li>
            </ul>
          </div>
        </div>

        <div className="callout">
          <h3 className="font-bold mb-2 text-orange-600">🎯 Pausen-Regel:</h3>
          <p>
            Besser häufige kurze Pausen als wenige lange. Ihr Rücken profitiert mehr von 6x 2 Minuten als von 1x 12 Minuten Pause.
          </p>
        </div>
      </section>

      <section id="lifestyle">
        <h2>Lifestyle-Faktoren für eine gesunde Wirbelsäule</h2>
        <p>
          Was Sie außerhalb des Büros tun, hat großen Einfluss auf Ihre Rückengesundheit:
        </p>

        <div className="grid-2">
          <div className="highlight-box">
            <h3 className="font-bold mb-3 text-green-600">🏃 Regelmäßiger Sport</h3>
            <p>Ideal für den Rücken:</p>
            <ul>
              <li>• Schwimmen (besonders Rückenschwimmen)</li>
              <li>• Pilates oder Yoga</li>
              <li>• Krafttraining für Rumpf und Rücken</li>
              <li>• Nordic Walking</li>
              <li>• Radfahren mit aufrechter Haltung</li>
            </ul>
          </div>
          <div className="highlight-box">
            <h3 className="font-bold mb-3 text-blue-600">😴 Gesunder Schlaf</h3>
            <p>Rückenfreundlich schlafen:</p>
            <ul>
              <li>• Mittelfeste Matratze verwenden</li>
              <li>• Seitenlage mit Kissen zwischen den Knien</li>
              <li>• Rückenlage mit Kissen unter den Knien</li>
              <li>• Bauchschlaf vermeiden</li>
              <li>• 7-8 Stunden Schlaf pro Nacht</li>
            </ul>
          </div>
        </div>

        <div className="grid-2">
          <div className="highlight-box">
            <h3 className="font-bold mb-3 text-purple-600">🥗 Richtige Ernährung</h3>
            <ul>
              <li>• Entzündungshemmende Lebensmittel</li>
              <li>• Omega-3-Fettsäuren (Fisch, Nüsse)</li>
              <li>• Viel Gemüse und Obst</li>
              <li>• Ausreichend Calcium und Vitamin D</li>
              <li>• Viel Wasser trinken (2-3 Liter täglich)</li>
            </ul>
          </div>
          <div className="highlight-box">
            <h3 className="font-bold mb-3 text-red-600">😌 Stressmanagement</h3>
            <ul>
              <li>• Entspannungstechniken erlernen</li>
              <li>• Meditation oder Achtsamkeit</li>
              <li>• Work-Life-Balance beachten</li>
              <li>• Hobbys pflegen</li>
              <li>• Ausreichend soziale Kontakte</li>
            </ul>
          </div>
        </div>
      </section>

      <section id="warnsignale">
        <h2>Warnsignale erkennen</h2>
        <p>
          Ihr Körper sendet Ihnen Signale, bevor es zu ernsteren Problemen kommt. Achten Sie auf:
        </p>

        <div className="callout">
          <h3 className="font-bold mb-3 text-red-600">🚨 Sofort handeln bei:</h3>
          <ul>
            <li className="flex items-start">
              <span className="text-red-500 mr-2 mt-1">⚠️</span>
              <span><strong>Ausstrahlende Schmerzen</strong> in Bein oder Arm</span>
            </li>
            <li className="flex items-start">
              <span className="text-red-500 mr-2 mt-1">⚠️</span>
              <span><strong>Taubheitsgefühle</strong> oder Kribbeln</span>
            </li>
            <li className="flex items-start">
              <span className="text-red-500 mr-2 mt-1">⚠️</span>
              <span><strong>Muskelschwäche</strong> in Armen oder Beinen</span>
            </li>
            <li className="flex items-start">
              <span className="text-red-500 mr-2 mt-1">⚠️</span>
              <span><strong>Schmerzen bei Husten oder Niesen</strong></span>
            </li>
            <li className="flex items-start">
              <span className="text-red-500 mr-2 mt-1">⚠️</span>
              <span><strong>Bewegungseinschränkungen</strong> am Morgen</span>
            </li>
          </ul>
          <p className="mt-4 font-semibold">
            → Bei diesen Symptomen sollten Sie umgehend einen Arzt aufsuchen!
          </p>
        </div>

        <div className="callout">
          <h3 className="font-bold mb-3 text-yellow-600">⚡ Frühe Warnsignale:</h3>
          <ul>
            <li>• Verspannungen, die länger als 3 Tage anhalten</li>
            <li>• Morgendliche Steifheit im Rücken</li>
            <li>• Schmerzen beim Bücken oder Drehen</li>
            <li>• Häufige Kopfschmerzen (oft durch Nackenverspannungen)</li>
            <li>• Müdigkeit trotz ausreichend Schlaf</li>
          </ul>
          <p className="mt-4">
            → Bei frühen Warnsignalen: Übungen intensivieren, Arbeitsplatz überprüfen, ggf. Physiotherapie
          </p>
        </div>
      </section>

      <section id="conclusion">
        <h2>Ihr Weg zu einem gesunden Rücken</h2>
        <p>
          Ein gesunder Rücken trotz 9-stündiger Büroarbeit ist absolut möglich. Der Schlüssel liegt in der Kombination aus ergonomischem Arbeitsplatz, regelmäßiger Bewegung und bewussten Pausen.
        </p>
        <p>
          Beginnen Sie nicht mit allem auf einmal. Wählen Sie 2-3 Maßnahmen aus, die Sie sofort umsetzen können, und bauen Sie nach und nach weitere Gewohnheiten auf. Ihr Rücken wird es Ihnen danken!
        </p>

        <div className="callout">
          <h3 className="font-bold mb-2 text-green-600">✅ Ihre nächsten Schritte:</h3>
          <ol className="list-decimal ml-6">
            <li><strong>Heute:</strong> Ergonomie-Check Ihres Arbeitsplatzes durchführen</li>
            <li><strong>Diese Woche:</strong> 30-Minuten-Regel einführen (Timer stellen!)</li>
            <li><strong>Nächste Woche:</strong> Täglich 3 Mini-Übungen am Arbeitsplatz</li>
            <li><strong>Nächster Monat:</strong> Regelmäßigen Sport in den Alltag integrieren</li>
            <li><strong>Langfristig:</strong> Rückenfreundliche Gewohnheiten zur Routine machen</li>
          </ol>
        </div>

        <div className="highlight-box" style={{textAlign:'center'}}>
          <h3 className="font-bold mb-2 text-blue-600">💪 Denken Sie daran:</h3>
          <p className="text-lg">
            Prävention ist immer besser und günstiger als Behandlung. Investieren Sie heute in Ihre Rückengesundheit!
          </p>
        </div>
      </section>

    </BlogArticleLayout>
  );
}
